import React from 'react';
import { Wrench, Users, Award, TrendingUp, Lightbulb, Microscope, Briefcase, Star } from 'lucide-react';
import '../Css_files/Mech.css';
import Navbar from './Navbar';
import Footer from './Footer';

const Mechanical = () => {
  const programs = [
    {
      title: 'Mechanical Engineering Fundamentals',
      duration: 'Self-Paced',
      description: 'Learn the core principles of mechanical engineering with interactive lessons, simulations, and projects designed for beginners and professionals.',
      features: ['Core Engineering Concepts', 'Virtual Labs & Simulations', 'Industry Projects', 'Completion Certificate']
    },
    {
      title: 'Advanced Manufacturing & Automation',
      duration: '6 Months',
      description: 'Master smart manufacturing, robotics, and automation with practical online training and real-world case studies.',
      features: ['Automation Systems', 'CAD/CAM Tools', 'Hands-on Projects', 'Industry Mentorship']
    },
    {
      title: 'Thermal Systems & Energy Engineering',
      duration: '6 Months',
      description: 'Explore thermodynamics, renewable energy, and sustainable technologies with practical online workshops.',
      features: ['Heat Transfer & CFD', 'Energy Systems', 'Sustainability Modules', 'Career-Focused Projects']
    }
  ];

  const faculty = [
    {
      name: 'Dr. Rajesh Kumar',
      position: 'Lead Instructor',
      specialization: 'Thermal Systems & Energy',
      experience: '15+ years in Teaching & Industry',
      image: 'https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=300'
    },
    {
      name: 'Dr. Priya Sharma',
      position: 'Senior Mentor',
      specialization: 'Automation & Smart Manufacturing',
      experience: '12+ years in Research & Training',
      image: 'https://images.pexels.com/photos/3182821/pexels-photo-3182821.jpeg?auto=compress&cs=tinysrgb&w=300'
    },
    {
      name: 'Dr. Amit Patel',
      position: 'Instructor',
      specialization: 'Robotics & Control Systems',
      experience: '8+ years in Industry Projects',
      image: 'https://images.pexels.com/photos/3182804/pexels-photo-3182804.jpeg?auto=compress&cs=tinysrgb&w=300'
    }
  ];

  const facilities = [
    {
      title: 'Virtual CAD/CAM Lab',
      description: 'Access advanced CAD/CAM tools and simulations online with guided tutorials.',
      icon: <Wrench className="mech-facility-icon" />
    },
    {
      title: 'Robotics & Automation Simulations',
      description: 'Practice real-world robotics and automation projects virtually.',
      icon: <Lightbulb className="mech-facility-icon" />
    },
    {
      title: 'Materials & Testing Demos',
      description: 'Interactive experiments and case studies on material strength and performance.',
      icon: <Microscope className="mech-facility-icon" />
    },
    {
      title: 'Thermal & Energy Modules',
      description: 'Online labs for thermal systems, CFD analysis, and energy efficiency studies.',
      icon: <TrendingUp className="mech-facility-icon" />
    }
  ];

  const careerStats = [
    { icon: <Users />, value: '50,000+', label: 'Learners Worldwide' },
    { icon: <Award />, value: '95%', label: 'Course Completion Rate' },
    { icon: <Briefcase />, value: '300+', label: 'Hiring Partners' },
    { icon: <Star />, value: '4.9/5', label: 'Student Satisfaction' }
  ];

  const testimonials = [
    {
      name: 'Arjun Krishnan',
      role: 'Mechanical Engineer, Tata Motors',
      year: 'Online Course Graduate',
      content: 'Profenaa’s mechanical engineering courses gave me practical knowledge and industry-ready skills. The projects were exactly what my job required.',
      image: 'https://images.pexels.com/photos/3182773/pexels-photo-3182773.jpeg?auto=compress&cs=tinysrgb&w=150'
    },
    {
      name: 'Sneha Reddy',
      role: 'Research Fellow, ISRO',
      year: 'Online Course Graduate',
      content: 'The virtual labs and research-based modules helped me apply concepts to real-world aerospace projects. Truly a world-class learning experience.',
      image: 'https://images.pexels.com/photos/3182824/pexels-photo-3182824.jpeg?auto=compress&cs=tinysrgb&w=150'
    }
  ];

  return (
    <>
      <Navbar /> <br /><br />
      <div className="mech-page">
        
        {/* Hero Section */}
        <section className="mech-hero">
          <div className="mech-hero-background">
            <div className="mech-animated-gears">
              <div className="mech-gear mech-gear-1"></div>
              <div className="mech-gear mech-gear-2"></div>
              <div className="mech-gear mech-gear-3"></div>
              <div className="mech-gear mech-gear-4"></div>
            </div>
          </div>
          <div className="mech-hero-overlay"></div>
          <div className="mech-hero-content">
            <h1 className="mech-hero-title">
              Mechanical Engineering Online Courses
              <span className="mech-hero-subtitle">Learn Anytime. Build Skills. Get Certified.</span>
            </h1>
            <p className="mech-hero-description">
              Master mechanical engineering from anywhere in the world. Gain hands-on skills through online labs, projects, and expert mentorship to power your career in engineering and technology.
            </p>
            <div className="mech-hero-actions">
              <button className="mech-btn mech-btn-primary">Start Learning</button>
              <button className="mech-btn mech-btn-secondary">View Courses</button>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="mech-stats">
          <div className="mech-container">
            <div className="mech-stats-grid">
              {careerStats.map((stat, index) => (
                <div key={index} className="mech-stat-card">
                  <div className="mech-stat-icon">{stat.icon}</div>
                  <div className="mech-stat-value">{stat.value}</div>
                  <div className="mech-stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Programs Section */}
        <section className="mech-programs">
          <div className="mech-container">
            <div className="mech-section-header">
              <h2 className="mech-section-title">Our Courses</h2>
              <p className="mech-section-subtitle">
                Flexible online programs designed to build real-world engineering skills.
              </p>
            </div>
            <div className="mech-programs-grid">
              {programs.map((program, index) => (
                <div key={index} className="mech-program-card">
                  <div className="mech-program-header">
                    <h3 className="mech-program-title">{program.title}</h3>
                    <span className="mech-program-duration">{program.duration}</span>
                  </div>
                  <p className="mech-program-description">{program.description}</p>
                  <ul className="mech-program-features">
                    {program.features.map((feature, idx) => (
                      <li key={idx} className="mech-program-feature">
                        <span className="mech-feature-bullet"></span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button className="mech-program-btn">Enroll Now</button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Faculty Section */}
        <section className="mech-faculty">
          <div className="mech-container">
            <div className="mech-section-header">
              <h2 className="mech-section-title">Meet Your Instructors</h2>
              <p className="mech-section-subtitle">
                Learn directly from experts with decades of industry and teaching experience.
              </p>
            </div>
            <div className="mech-faculty-grid">
              {faculty.map((member, index) => (
                <div key={index} className="mech-faculty-card">
                  <div className="mech-faculty-image">
                    <img src={member.image} alt={member.name} />
                    <div className="mech-faculty-overlay">
                      <div className="mech-faculty-experience">{member.experience}</div>
                    </div>
                  </div>
                  <div className="mech-faculty-info">
                    <h3 className="mech-faculty-name">{member.name}</h3>
                    <p className="mech-faculty-position">{member.position}</p>
                    <p className="mech-faculty-specialization">{member.specialization}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Facilities Section */}
        <section className="mech-facilities">
          <div className="mech-container">
            <div className="mech-section-header">
              <h2 className="mech-section-title">Learning Tools</h2>
              <p className="mech-section-subtitle">
                Experience industry-level tools and simulations right from your home.
              </p>
            </div>
            <div className="mech-facilities-grid">
              {facilities.map((facility, index) => (
                <div key={index} className="mech-facility-card">
                  <div className="mech-facility-icon-container">
                    {facility.icon}
                  </div>
                  <h3 className="mech-facility-title">{facility.title}</h3>
                  <p className="mech-facility-description">{facility.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Research Section */}
        <section className="mech-research">
          <div className="mech-container">
            <div className="mech-research-content">
              <div className="mech-research-text">
                <h2 className="mech-research-title">Projects & Innovation</h2>
                <p className="mech-research-description">
                  Work on real-world projects guided by experts. From renewable energy to robotics, apply your skills to future-ready technologies.
                </p>
                <div className="mech-research-highlights">
                  <div className="mech-research-highlight">
                    <h4>Hands-on Projects</h4>
                    <p>Build 10+ portfolio-ready engineering projects.</p>
                  </div>
                  <div className="mech-research-highlight">
                    <h4>Industry Case Studies</h4>
                    <p>Learn from real-world challenges and solutions.</p>
                  </div>
                  <div className="mech-research-highlight">
                    <h4>Career Skills</h4>
                    <p>Get trained in problem-solving, design, and innovation.</p>
                  </div>
                </div>
              </div>
              <div className="mech-research-visual">
                <div className="mech-research-animation">
                  <div className="mech-research-circle mech-research-circle-1"></div>
                  <div className="mech-research-circle mech-research-circle-2"></div>
                  <div className="mech-research-circle mech-research-circle-3"></div>
                  <div className="mech-research-center">
                    <Microscope className="mech-research-icon" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="mech-testimonials">
          <div className="mech-container">
            <div className="mech-section-header">
              <h2 className="mech-section-title">What Our Students Say</h2>
              <p className="mech-section-subtitle">
                Hear from learners who transformed their careers with Profenaa.
              </p>
            </div>
            <div className="mech-testimonials-grid">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="mech-testimonial-card">
                  <div className="mech-testimonial-content">
                    <p className="mech-testimonial-text">"{testimonial.content}"</p>
                  </div>
                  <div className="mech-testimonial-author">
                    <img src={testimonial.image} alt={testimonial.name} className="mech-testimonial-avatar" />
                    <div className="mech-testimonial-info">
                      <h4 className="mech-testimonial-name">{testimonial.name}</h4>
                      <p className="mech-testimonial-role">{testimonial.role}</p>
                      <p className="mech-testimonial-year">{testimonial.year}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="mech-cta">
          <div className="mech-container">
            <div className="mech-cta-content">
              <h2 className="mech-cta-title">Start Your Learning Journey Today</h2>
              <p className="mech-cta-description">
                Join Profenaa Technologies and master mechanical engineering online. Learn anytime, from anywhere, with expert guidance and career-focused training.
              </p>
              <div className="mech-cta-actions">
                <button className="mech-btn mech-btn-primary mech-btn-large">Enroll Now</button>
                <button className="mech-btn mech-btn-outline mech-btn-large">Get Free Demo</button>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Mechanical;
